import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Card,
  Typography,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Select,
  Checkbox,
  message,
  Spin,
  Tag,
  Tooltip,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SaveOutlined,
  CloseOutlined,
  ReloadOutlined,
  DownOutlined,
  RightOutlined,
} from "@ant-design/icons";
import {
  DragOverlay,
  closestCorners,
  DndContext,
  PointerSensor,
  pointerWithin,
  rectIntersection,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import { useAppDispatch } from "../../hooks/reduxHooks";
import {
  getProductHierarchyApi,
  manageProductHierarchyApi,
  moveProductHierarchyNodeApi,
  deleteProductHierarchyNodeApi,
} from "../../services/products.service";
import {
  cloneHierarchyNodes,
  findHierarchyNodeMeta,
  formatHierarchyNodeName,
  getHierarchyNodeChildren,
  getHierarchyNodeDetailFields,
  getHierarchyNodeKey,
  isHierarchyProductNode,
  moveHierarchyNodeInTree,
} from "../../utils/productHierarchy.utils";

const { Title } = Typography;
const { TextArea } = Input;

const DROP_TYPE = {
  INTO: "into",
  ROOT_END: "root-end",
};

const hierarchyCollisionDetection = (args) => {
  let pointerCollisions = pointerWithin(args);
  if (pointerCollisions.length === 0) {
    pointerCollisions = rectIntersection(args);
  }

  if (pointerCollisions.length > 0) {
    // Prefer the most specific (innermost) droppable.
    const nodeDropTargets = pointerCollisions.filter(
      (c) => String(c.id).startsWith("drop-into-")
    );
    if (nodeDropTargets.length > 0) {
      // Reversing ensures that the last element in the DOM (the most nested node)
      // comes first, making it the primary collision target.
      const reversedTargets = [...nodeDropTargets].reverse();
      return reversedTargets;
    }
    return pointerCollisions;
  }

  return closestCorners(args);
};

const toHashKey = (value) => String(value);

const DropZone = ({ id, data, activeDragNodeId, style, children }) => {
  const { setNodeRef, isOver } = useDroppable({ id, data });
  const isDragging = Boolean(activeDragNodeId);
  const isHighlighted = isOver && isDragging;
  const hasChildren = React.Children.count(children) > 0;
  const baseHeight = style?.height ?? 8;

  return (
    <div
      ref={setNodeRef}
      style={{
        ...style,
        height: hasChildren ? style?.height : isDragging ? baseHeight : 0,
        minHeight: hasChildren ? (style?.minHeight ?? 24) : undefined,
        borderRadius: style?.borderRadius ?? 8,
        transition: "all 0.15s ease",
        backgroundColor: isHighlighted
          ? "#e6f4ff"
          : style?.backgroundColor || "transparent",
        boxShadow: isHighlighted
          ? "0 0 0 2px #91caff inset"
          : style?.boxShadow,
      }}
    >
      {children}
    </div>
  );
};

const DraggableDroppableNode = ({
  nodeId,
  disabled = false,
  activeDragNodeId,
  onDropHoverChange,
  children,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef: setDragNodeRef,
    transform,
    isDragging,
  } = useDraggable({
    id: `drag-${nodeId}`,
    data: { type: "node", nodeId },
    disabled,
  });

  const { setNodeRef: setDropNodeRef, isOver } = useDroppable({
    id: `drop-into-${nodeId}`,
    data: { type: "drop", dropType: DROP_TYPE.INTO, targetId: nodeId },
    disabled: activeDragNodeId === nodeId, // disable self-drop
  });

  const isValidDropTarget =
    isOver && Boolean(activeDragNodeId) && activeDragNodeId !== nodeId;

  const dropStyle = {
    borderRadius: 8,
    overflow: "hidden",
    transition: "background-color 0.15s ease, box-shadow 0.15s ease",
    backgroundColor: isValidDropTarget ? "#e6f4ff" : "transparent",
    boxShadow: isValidDropTarget ? "0 0 0 2px #91caff inset" : undefined,
  };

  const dragStyle = {
    cursor: disabled ? "default" : "grab",
  };

  useEffect(() => {
    if (typeof onDropHoverChange === "function") {
      onDropHoverChange(nodeId, isOver && activeDragNodeId !== nodeId);
    }
  }, [isOver, nodeId, activeDragNodeId, onDropHoverChange]);

  return (
    <div ref={setDropNodeRef} style={dropStyle}>
      <div
        ref={setDragNodeRef}
        style={dragStyle}
        {...(disabled ? {} : attributes)}
        {...(disabled ? {} : listeners)}
      >
        {children}
      </div>
    </div>
  );
};

const ProductHierarchy = () => {
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);
  const [hierarchyData, setHierarchyData] = useState([]);
  const [deleteConfirmVisible, setDeleteConfirmVisible] = useState(false);
  const [nodeToDelete, setNodeToDelete] = useState(null);
  const [deleteProductWithNode, setDeleteProductWithNode] = useState(false);
  const [editingNodeHashId, setEditingNodeHashId] = useState(null);
  const [addingToNodeHashId, setAddingToNodeHashId] = useState(null);
  const [editFormData, setEditFormData] = useState({ name: '', description: '', tags: [] });
  const [addFormData, setAddFormData] = useState({ name: '', description: '', tags: [] });
  const [rootModalVisible, setRootModalVisible] = useState(false);
  const [rootFormData, setRootFormData] = useState({ name: '', description: '', tags: [] });
  const [activeDragNodeId, setActiveDragNodeId] = useState(null);
  const [hoveredDropParentNodeId, setHoveredDropParentNodeId] = useState(null);
  const [collapsedNodes, setCollapsedNodes] = useState({});
  const expandTimerRef = useRef(null);
  const [reorderSaving, setReorderSaving] = useState(false);
  const [form] = Form.useForm();
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 3 },
    })
  );

  useEffect(() => {
    fetchHierarchy();
  }, []);

  const fetchHierarchy = () => {
    setLoading(true);
    dispatch(
      getProductHierarchyApi({
        finalCallback: () => setLoading(false),
        successCallback: (response) => {
          if (response?.hierarchy) {
            setHierarchyData(response.hierarchy);
          }
        },
        failureCallback: (error) => {
          message.error("Failed to fetch hierarchy data");
          console.error(error);
        },
      })
    );
  };

  const handleAdd = (node) => {
    setAddingToNodeHashId(getHierarchyNodeKey(node));
    setAddFormData({ name: '', description: '', tags: [] });
  };

  const handleEdit = (node) => {
    setEditingNodeHashId(getHierarchyNodeKey(node));
    setEditFormData({
      name: formatHierarchyNodeName(node.name),
      description: node.description || "",
      tags: node.tags || [],
    });
  };

  const handleCancelEdit = () => {
    setEditingNodeHashId(null);
    setEditFormData({ name: '', description: '', tags: [] });
  };

  const handleCancelAdd = () => {
    setAddingToNodeHashId(null);
    setAddFormData({ name: '', description: '', tags: [] });
  };

  const handleSaveEdit = (node) => {
    if (!editFormData.name.trim()) {
      message.error('Product name is required');
      return;
    }

    const payload = {
      action: "update",
      hash_id: node.hash_id || getHierarchyNodeKey(node),
      name: editFormData.name,
      description: editFormData.description || "",
      tags: editFormData.tags || [],
      ...(node.product_id && { product_id: node.product_id }),
    };

    dispatch(
      manageProductHierarchyApi({
        data: payload,
        finalCallback: () => { },
        successCallback: (response) => {
          message.success("Product updated successfully");
          setEditingNodeHashId(null);
          setEditFormData({ name: '', description: '', tags: [] });
          fetchHierarchy();
        },
        failureCallback: (error) => {
          message.error("Failed to update product");
          console.error(error);
        },
      })
    );
  };

  const handleSaveAdd = (parentNode) => {
    if (!addFormData.name.trim()) {
      message.error('Product name is required');
      return;
    }

    const payload = {
      action: "add",
      parent_hash_id: parentNode.hash_id || getHierarchyNodeKey(parentNode),
      name: addFormData.name,
      description: addFormData.description || "",
      tags: addFormData.tags || [],
    };

    dispatch(
      manageProductHierarchyApi({
        data: payload,
        finalCallback: () => { },
        successCallback: (response) => {
          message.success("Product added successfully");
          setAddingToNodeHashId(null);
          setAddFormData({ name: '', description: '', tags: [] });
          fetchHierarchy();
        },
        failureCallback: (error) => {
          message.error("Failed to add product");
          console.error(error);
        },
      })
    );
  };

  const handleRootModalOk = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        action: "add",
        parent_hash_id: null,
        name: values.name,
        description: values.description || "",
        tags: values.tags || [],
      };

      dispatch(
        manageProductHierarchyApi({
          data: payload,
          finalCallback: () => { },
          successCallback: (response) => {
            message.success("Root category added successfully");
            setRootModalVisible(false);
            setRootFormData({ name: '', description: '', tags: [] });
            form.resetFields();
            fetchHierarchy();
          },
          failureCallback: (error) => {
            message.error("Failed to add category");
            console.error(error);
          },
        })
      );
    } catch (error) {
      console.error("Validation failed:", error);
    }
  };

  const handleRootModalCancel = () => {
    setRootModalVisible(false);
    setRootFormData({ name: '', description: '', tags: [] });
    form.resetFields();
  };

  const handleDelete = (node) => {
    setNodeToDelete(node);
    setDeleteProductWithNode(false);
    setDeleteConfirmVisible(true);
  };

  const handleConfirmDelete = () => {
    if (nodeToDelete) {
      dispatch(
        deleteProductHierarchyNodeApi({
          hash_id: nodeToDelete.hash_id || getHierarchyNodeKey(nodeToDelete),
          delete_product: deleteProductWithNode,
          finalCallback: () => { },
          successCallback: (response) => {
            message.success(
              deleteProductWithNode
                ? "Hierarchy node and product deleted successfully"
                : "Hierarchy node deleted successfully"
            );
            fetchHierarchy();
            setDeleteConfirmVisible(false);
            setNodeToDelete(null);
            setDeleteProductWithNode(false);
          },
          failureCallback: (error) => {
            message.error("Failed to delete hierarchy node");
            console.error(error);
          },
        })
      );
    }
  };

  const handleCancelDelete = () => {
    setDeleteConfirmVisible(false);
    setNodeToDelete(null);
    setDeleteProductWithNode(false);
  };

  const handleDragStart = (event) => {
    const activeNodeId = event?.active?.data?.current?.nodeId;
    setActiveDragNodeId(activeNodeId || null);
    setHoveredDropParentNodeId(null);
    if (expandTimerRef.current) {
      clearTimeout(expandTimerRef.current);
      expandTimerRef.current = null;
    }
  };

  const handleDragCancel = () => {
    setActiveDragNodeId(null);
    setHoveredDropParentNodeId(null);
    if (expandTimerRef.current) {
      clearTimeout(expandTimerRef.current);
      expandTimerRef.current = null;
    }
  };

  const handleDragEnd = ({ active, over }) => {
    if (expandTimerRef.current) {
      clearTimeout(expandTimerRef.current);
      expandTimerRef.current = null;
    }

    if (reorderSaving) {
      setActiveDragNodeId(null);
      return;
    }

    const activeNodeId = active?.data?.current?.nodeId;
    const dropData = over?.data?.current;
    setActiveDragNodeId(null);
    setHoveredDropParentNodeId(null);

    if (!activeNodeId || !dropData || dropData.type !== "drop") {
      return;
    }

    const sourceMeta = findHierarchyNodeMeta(hierarchyData, activeNodeId);
    const targetMeta =
      dropData.dropType === DROP_TYPE.ROOT_END
        ? null
        : findHierarchyNodeMeta(hierarchyData, dropData.targetId);
    const sourceName = formatHierarchyNodeName(sourceMeta?.node?.name) || "Current item";
    let destinationName = "Root level";
    if (dropData.dropType === DROP_TYPE.INTO) {
      destinationName = formatHierarchyNodeName(targetMeta?.node?.name) || "Selected item";
    }

    const previousHierarchy = cloneHierarchyNodes(hierarchyData);
    const nextHierarchy = cloneHierarchyNodes(hierarchyData);
    const { moved, invalidCycle, move } = moveHierarchyNodeInTree({
      tree: nextHierarchy,
      activeNodeKey: activeNodeId,
      dropData,
      dropTypes: DROP_TYPE,
    });

    if (invalidCycle) {
      message.warning("Parent cannot move to its own child.");
      return;
    }

    if (moved) {
      Modal.confirm({
        title: "Confirm move",
        content: `Are you sure you want to move "${sourceName}" to "${destinationName}"?`,
        okText: "Move",
        cancelText: "Cancel",
        onOk: () => {
          setHierarchyData(nextHierarchy);
          setReorderSaving(true);
          dispatch(
            moveProductHierarchyNodeApi({
              data: {
                hash_id: move?.hash_id,
                parent_hash_id: move?.parent_hash_id,
                position: move?.position,
                previous_parent_hash_id: move?.previous_parent_hash_id,
                previous_position: move?.previous_position,
              },
              finalCallback: () => setReorderSaving(false),
              successCallback: () => { },
              failureCallback: (error) => {
                setHierarchyData(previousHierarchy);
                message.error(
                  error?.response?.data?.detail ||
                  "Failed to save hierarchy order. Changes reverted."
                );
              },
            })
          );
        },
      });
    }
  };

  const handleDropHoverChange = useCallback((hoveredNodeId, isOverNode) => {
    if (expandTimerRef.current) {
      clearTimeout(expandTimerRef.current);
      expandTimerRef.current = null;
    }

    if (!isOverNode) {
      setHoveredDropParentNodeId((prev) =>
        prev === hoveredNodeId ? null : prev
      );
      return;
    }
    setHoveredDropParentNodeId(hoveredNodeId);

    // Auto-expand collapsed node after hovering for 600ms
    expandTimerRef.current = setTimeout(() => {
      setCollapsedNodes((prev) => {
        if (prev[hoveredNodeId]) {
          const next = { ...prev };
          delete next[hoveredNodeId];
          return next;
        }
        return prev;
      });
    }, 600);
  }, []);

  const renderNodeActions = (node, isEditing) => (
    <Space size={8} onClick={(e) => e.stopPropagation()}>
      {isEditing ? (
        <>
          <Button
            type="primary"
            size="small"
            icon={<SaveOutlined />}
            onClick={() => handleSaveEdit(node)}
            style={{ background: '#1677ff', borderColor: '#1677ff', color: '#ffffff' }}
          >
            Save
          </Button>
          <Button
            type="link"
            size="small"
            icon={<CloseOutlined />}
            onClick={handleCancelEdit}
          >
            Cancel
          </Button>
        </>
      ) : (
        <>
          <Tooltip title="Edit">
            <Button
              type="link"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEdit(node)}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Button
              type="link"
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                handleDelete(node);
              }}
            />
          </Tooltip>
        </>
      )}
    </Space>
  );

  const toggleCollapse = (nodeId) => {
    setCollapsedNodes((prev) => ({
      ...prev,
      [nodeId]: !prev[nodeId],
    }));
  };

  const renderHierarchyNode = (node, level = 0) => {
    const childNodes = getHierarchyNodeChildren(node);
    const hasChildren = childNodes.length > 0;
    const hasProductId = isHierarchyProductNode(node);
    const nodeKey = getHierarchyNodeKey(node);
    const isEditing = editingNodeHashId === nodeKey;
    const isAddingChild = addingToNodeHashId === nodeKey;
    const nodeId = toHashKey(nodeKey);
    const detailFields = getHierarchyNodeDetailFields(node);
    const isDraggingThisNode = activeDragNodeId === nodeId;
    const dragDisabled = reorderSaving || isEditing || isAddingChild;
    const isCollapsed = Boolean(collapsedNodes[nodeId]);
    const isDropParentHighlighted =
      Boolean(activeDragNodeId) &&
      activeDragNodeId !== nodeId &&
      hoveredDropParentNodeId === nodeId;

    return (
      <div
        key={nodeKey}
        style={{
          marginLeft: level * 20,
          marginBottom: 12,
          opacity: isDraggingThisNode ? 0.4 : 1,
          transition: "opacity 0.2s ease"
        }}
      >
        <DraggableDroppableNode
          nodeId={nodeId}
          disabled={dragDisabled}
          activeDragNodeId={activeDragNodeId}
          onDropHoverChange={handleDropHoverChange}
        >
          <div
            style={{
              border: isDropParentHighlighted
                ? "1px solid #91caff"
                : "1px solid #e8e8e8",
              borderRadius: 8,
              padding: 16,
              backgroundColor: isDropParentHighlighted
                ? "#e6f4ff"
                : level === 0
                  ? "#fafafa"
                  : "#ffffff",
              boxShadow: isDropParentHighlighted
                ? "0 0 0 1px rgba(24, 144, 255, 0.18), 0 4px 12px rgba(24, 144, 255, 0.12)"
                : "none",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "start",
              }}
            >
              <div style={{ flex: 1 }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    marginBottom: 8,
                  }}
                >
                  {isEditing ? (
                    <Input
                      value={editFormData.name}
                      onChange={(e) =>
                        setEditFormData({ ...editFormData, name: e.target.value })
                      }
                      placeholder="Product name"
                      style={{ flex: 1 }}
                    />
                  ) : (
                    <>
                      {hasChildren && (
                        <span
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleCollapse(nodeId);
                          }}
                          style={{
                            cursor: "pointer",
                            marginRight: 8,
                            fontSize: 12,
                            color: "#999",
                            display: "inline-flex",
                            alignItems: "center",
                            userSelect: "none",
                          }}
                        >
                          {isCollapsed ? <RightOutlined /> : <DownOutlined />}
                        </span>
                      )}
                      <Title level={level === 0 ? 4 : 5} style={{ margin: 0 }}>
                        {formatHierarchyNodeName(node.name)}
                      </Title>
                      {hasProductId && (
                        <Tag color="blue" style={{ marginLeft: 8 }}>
                          Product
                        </Tag>
                      )}
                    </>
                  )}
                </div>
                <div style={{ marginTop: 12 }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #f0f0f0' }}>
                    <tbody>
                      <tr>
                        <td style={{ width: '140px', fontWeight: 600, backgroundColor: '#fafafa', padding: '8px 16px', border: '1px solid #f0f0f0' }}>
                          Description
                        </td>
                        <td style={{ padding: '8px 16px', border: '1px solid #f0f0f0', wordBreak: 'break-word' }}>
                          {isEditing ? (
                            <TextArea
                              rows={3}
                              value={editFormData.description}
                              onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                              placeholder="Enter description (optional)"
                              style={{ width: '100%' }}
                            />
                          ) : (
                            node.description || "-"
                          )}
                        </td>
                      </tr>
                      <tr>
                        <td style={{ width: '140px', fontWeight: 600, backgroundColor: '#fafafa', padding: '8px 16px', border: '1px solid #f0f0f0' }}>
                          Tags
                        </td>
                        <td style={{ padding: '8px 16px', border: '1px solid #f0f0f0', wordBreak: 'break-word' }}>
                          {isEditing ? (
                            <Select
                              mode="tags"
                              value={editFormData.tags}
                              onChange={(tags) => setEditFormData({ ...editFormData, tags })}
                              placeholder="Enter tags (press Enter to add)"
                              style={{ width: '100%' }}
                            />
                          ) : (
                            Array.isArray(node.tags) && node.tags.length > 0
                              ? node.tags.map((tag, index) => (
                                <Tag key={index} color="green">
                                  {tag}
                                </Tag>
                              ))
                              : "-"
                          )}
                        </td>
                      </tr>
                      {!isEditing && detailFields
                        .filter((field) => field.label !== "Description" && field.label !== "Tags")
                        .map((field) => (
                          <tr key={field.label}>
                            <td style={{ width: '140px', fontWeight: 600, backgroundColor: '#fafafa', padding: '8px 16px', border: '1px solid #f0f0f0' }}>
                              {field.label}
                            </td>
                            <td style={{ padding: '8px 16px', border: '1px solid #f0f0f0', wordBreak: 'break-word' }}>
                              {field.value}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
              {renderNodeActions(node, isEditing)}
            </div>
            {isAddingChild && (
              <div style={{ marginTop: 16, padding: 16, backgroundColor: '#f0f2f5', borderRadius: 8 }}>
                <div style={{ marginBottom: 12 }}>
                  <Input
                    value={addFormData.name}
                    onChange={(e) => setAddFormData({ ...addFormData, name: e.target.value })}
                    placeholder="Product name *"
                  />
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #f0f0f0', marginBottom: 12 }}>
                  <tbody>
                    <tr>
                      <td style={{ width: '140px', fontWeight: 600, backgroundColor: '#fafafa', padding: '8px 16px', border: '1px solid #f0f0f0' }}>
                        Description
                      </td>
                      <td style={{ padding: '8px 16px', border: '1px solid #f0f0f0', wordBreak: 'break-word' }}>
                        <TextArea
                          rows={2}
                          value={addFormData.description}
                          onChange={(e) => setAddFormData({ ...addFormData, description: e.target.value })}
                          placeholder="Description (optional)"
                          style={{ width: '100%' }}
                        />
                      </td>
                    </tr>
                    <tr>
                      <td style={{ width: '140px', fontWeight: 600, backgroundColor: '#fafafa', padding: '8px 16px', border: '1px solid #f0f0f0' }}>
                        Tags
                      </td>
                      <td style={{ padding: '8px 16px', border: '1px solid #f0f0f0', wordBreak: 'break-word' }}>
                        <Select
                          mode="tags"
                          value={addFormData.tags}
                          onChange={(tags) => setAddFormData({ ...addFormData, tags })}
                          placeholder="Tags (press Enter to add)"
                          style={{ width: '100%' }}
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
                <div style={{ textAlign: 'right' }}>
                  <Space>
                    <Button
                      type="primary"
                      icon={<SaveOutlined />}
                      onClick={() => handleSaveAdd(node)}
                      size="small"
                    >
                      Save
                    </Button>
                    <Button
                      icon={<CloseOutlined />}
                      onClick={handleCancelAdd}
                      size="small"
                    >
                      Cancel
                    </Button>
                  </Space>
                </div>
              </div>
            )}

            <div style={{ marginTop: isAddingChild ? 16 : 8, textAlign: 'center' }}>
              <Button
                type="dashed"
                icon={<PlusOutlined />}
                onClick={() => handleAdd(node)}
                disabled={isAddingChild}
                style={{
                  width: '100%',
                  borderColor: isAddingChild ? '#d9d9d9' : '#1890ff',
                  color: isAddingChild ? '#d9d9d9' : '#1890ff',
                  borderStyle: 'dashed'
                }}
              >
                Add product to {formatHierarchyNodeName(node.name)}
              </Button>
            </div>
          </div>
        </DraggableDroppableNode>
        {hasChildren && !isCollapsed && (
          <div style={{ marginTop: 16 }}>
            {childNodes.map((childNode) =>
              renderHierarchyNode(childNode, level + 1)
            )}
          </div>
        )}

      </div>
    );
  };

  const activeDragNodeMeta = activeDragNodeId
    ? findHierarchyNodeMeta(hierarchyData, activeDragNodeId)
    : null;
  const activeDragNode = activeDragNodeMeta?.node;

  return (
    <div style={{ padding: 24 }}>
      <Card>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 24,
          }}
        >
          <Title level={2} style={{ margin: 0 }}>
            Product Hierarchy Management
          </Title>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={fetchHierarchy}>
              Refresh
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setRootModalVisible(true)}
            >
              Add Category
            </Button>
          </Space>
        </div>

        <Spin spinning={loading}>
          {hierarchyData.length === 0 && !loading ? (
            <div
              style={{
                textAlign: "center",
                padding: 48,
                color: "#999",
              }}
            >
              <Typography.Text>
                No hierarchy data available. Click "Add Category" to get
                started.
              </Typography.Text>
            </div>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={hierarchyCollisionDetection}
              onDragStart={handleDragStart}
              onDragCancel={handleDragCancel}
              onDragEnd={handleDragEnd}
            >
              <DropZone
                id="drop-root-end"
                data={{ type: "drop", dropType: DROP_TYPE.ROOT_END }}
                activeDragNodeId={activeDragNodeId}
                style={{ borderRadius: 10, padding: 4, minHeight: "100%", height: "100%" }}
              >
                <div style={{ minHeight: "100%" }}>
                  {hierarchyData.map((node) => renderHierarchyNode(node, 0))}
                </div>
              </DropZone>
              <DragOverlay dropAnimation={{
                duration: 250,
                easing: 'cubic-bezier(0.18, 0.67, 0.6, 1.22)',
              }}>
                {activeDragNode ? (
                  <div
                    style={{
                      border: "1px solid #1890ff",
                      borderRadius: 8,
                      padding: 16,
                      backgroundColor: "#ffffff",
                      boxShadow: "0 8px 24px rgba(24, 144, 255, 0.3)",
                      display: "flex",
                      alignItems: "center",
                      pointerEvents: "none",
                      opacity: 0.95
                    }}
                  >
                    <Title level={5} style={{ margin: 0 }}>
                      {formatHierarchyNodeName(activeDragNode.name)}
                    </Title>
                    {isHierarchyProductNode(activeDragNode) && (
                      <Tag color="blue" style={{ marginLeft: 8 }}>
                        Product
                      </Tag>
                    )}
                  </div>
                ) : null}
              </DragOverlay>
            </DndContext>
          )}
        </Spin>
      </Card>

      <Modal
        title="Delete Hierarchy Item"
        open={deleteConfirmVisible}
        onOk={handleConfirmDelete}
        onCancel={handleCancelDelete}
        okText="Delete"
        cancelText="Cancel"
        okButtonProps={{ danger: true }}
        centered
      >
        <p>
          Are you sure you want to delete{" "}
          <strong>{formatHierarchyNodeName(nodeToDelete?.name) || "this hierarchy item"}</strong>? This action cannot be undone.
        </p>
        <div style={{ marginTop: 12 }}>
          <Checkbox
            checked={deleteProductWithNode}
            onChange={(e) => setDeleteProductWithNode(e.target.checked)}
          >
            Also delete linked products from product list
          </Checkbox>
        </div>
      </Modal>

      <Modal
        title="Add Category"
        open={rootModalVisible}
        onOk={handleRootModalOk}
        onCancel={handleRootModalCancel}
        width={600}
        centered
        okText="Add"
      >
        <Form form={form} layout="vertical" style={{ marginTop: 24 }}>
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: "Please enter a category name" }]}
          >
            <Input placeholder="Enter category name" />
          </Form.Item>

          <Form.Item name="description" label="Description">
            <TextArea
              rows={3}
              placeholder="Enter description (optional)"
            />
          </Form.Item>

          <Form.Item name="tags" label="Tags">
            <Select
              mode="tags"
              placeholder="Enter tags (press Enter to add)"
              style={{ width: "100%" }}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ProductHierarchy;
